import type { AppSettings } from '../stores/types/settings';
import type { Shot } from '../stores/types/shots';
import { FEED_TYPES } from '../stores/reducers/shots';
import { HttpRequest } from '../stores/types/types';
import { Entity } from '../types/interfaces';
import { getAllShotOverlays } from './graphics';
import { buildSyncUrl, getAuthenticatedRequestInit, getSyncHeaders } from './utils';
import { getEmbedSafeHeaders } from './auth';
import blpAuth from './blp-auth';
import { getApiHost, getSyncHost } from '../utils/hosts';
import logger from '../utils/logger-rum';

export const LoadShots = (
	token: string,
	meetingId: number,
) => fetch(`${getApiHost()}/v2/greenroom/shots/load`, {
	method: 'POST',
	body: JSON.stringify({ meetingId }),
	...getAuthenticatedRequestInit(token),
}).then(async (result) => {
	if (result.ok) {
		const data = await result.json();
		return data;
	}
	throw new Error(`Could not load shots for meeting id ${meetingId}`);
});

export const LoadShot = (token: string, meetingId: number, shotId: number) => fetch(`${getSyncHost()}/v1/room/${meetingId}/load-shot/${shotId}`, {
	method: 'GET',
	headers: getSyncHeaders(token),
}).then(async (result) => {
	if (result.ok) {
		const data = await result.json();
		return data;
	}
	throw new Error(`Could not load shots for meeting id ${meetingId}`);
});

export const FetchShot = (token: string, meetingId: number, shotId: number) => fetch(`${getApiHost()}/v2/greenroom/shot/load`, {
	method: 'POST',
	body: JSON.stringify({ meetingId, shotId }),
	...getAuthenticatedRequestInit(token),
}).then(async (result) => {
	if (result.ok) {
		const data = await result.json();
		return data;
	}
	throw new Error(`Could not load shot ${shotId} for meeting id ${meetingId}`);
});

export const AddShot = (token: string, shot: Shot, session: string): Promise<Shot> => {
	const meetingId = shot.meeting;
	return fetch(`${getApiHost()}/v2/greenroom/shots/add`, {
		method: 'POST',
		body: JSON.stringify({ shot, meetingId, session }),
		...getAuthenticatedRequestInit(token),
	}).then((result) => {
		if (result.ok) {
			return result.json();
		}
		throw new Error('Unable to create shot');
	}).catch((error) => {
		logger.error(error);
	});
};

export const GenerateShotThumbnail = (
	token: string,
	meetingSlug: string,
	shotId: number,
	channelId: number,
	room?: number,
): Promise<void> => {
	const url = buildSyncUrl('v1/shots/generate');
	return fetch(url, {
		method: HttpRequest.Post,
		headers: getSyncHeaders(token),
		body: JSON.stringify({
			meetingId: meetingSlug,
			shotId,
			channelId,
			room,
		}),
	}).then(async (result) => {
		if (!result.ok) {
			throw new Error('Failed to generate shot thumbnail');
		}
	}).catch((error) => {
		logger.error(error);
		throw error;
	});
};

export const GenerateAllShotThumbnails = (
	token: string,
	meetingSlug: string,
	channelId: number,
	room: number,
	onlyMissing?: boolean,
): Promise<void> => {
	const url = buildSyncUrl('v1/shots/generate-all');
	return fetch(url, {
		method: HttpRequest.Post,
		headers: getSyncHeaders(token),
		body: JSON.stringify({
			meetingId: meetingSlug,
			channelId: channelId,
			room: room,
			...(onlyMissing && { onlyMissing }),
		}),
	}).then(async (result) => {
		if (!result.ok) {
			throw new Error('Failed to generate all shot thumbnails');
		}
	}).catch((error) => {
		logger.error(error);
		throw error;
	});
};

export const EditShot = (
	token: string,
	shot: Shot,
	meetingSlug?: string,
	channelId?: number,
	useNewShotThumbs: boolean = false,
	skipThumbnailRegen: boolean = false,
) => {
	const meetingId = shot.meeting;
	return fetch(`${getApiHost()}/v2/greenroom/shots/edit`, {
		method: 'POST',
		body: JSON.stringify({ shot, meetingId, skipThumbnailRegen }),
		...getAuthenticatedRequestInit(token),
	}).then(async (result) => {
		if (!result.ok) {
			throw new Error('Failed to edit shot');
		} else {
			// Get updated overlays when the shot is edited
			// There may be a better way to do this
			// Does this do nothing?
			getAllShotOverlays(token, meetingId, true).catch((err) => {
				logger.error(err);
			});

			const hasContent = shot.shot_data.shotFeeds?.some(
				(feed) => !feed.audioOnly && feed.feedType !== FEED_TYPES.EMPTY
			);

			if (!hasContent || !meetingSlug || !channelId || !useNewShotThumbs || skipThumbnailRegen) return;

			// Generate shot thumbnail if meeting slug and channel are provided and if the shot is not empty
			GenerateShotThumbnail(
				token,
				meetingSlug,
				shot.shot_id,
				channelId,
				meetingId, // room
			).catch((err) => {
				logger.error(err);
			});

		}
	}).catch((error) => {
		logger.error(error);
	});
};

export const BulkEditShots = (token: string, shots: Shot[], deprecated: string = ''): Promise<void> => {
	const meetingId = shots[0]?.meeting ?? 0;
	return fetch(`${getApiHost()}/v2/greenroom/shots/bulk-edit`, {
		method: 'POST',
		body: JSON.stringify({ shots, meetingId, deprecated }),
		...getAuthenticatedRequestInit(token),
	}).then(async (result) => {
		if (!result.ok) {
			throw new Error('Failed bulk editing shots');
		}
	}).catch((error) => {
		logger.error(error);
	});
};

export const DeleteShot = (token: string, shotId: number, meetingId: number) => fetch(`${getApiHost()}/v2/greenroom/shots/delete`, {
	method: 'POST',
	body: JSON.stringify({ shotId, meetingId }),
	...getAuthenticatedRequestInit(token),
}).then(async (result) => {
	if (!result.ok) {
		throw new Error('Failed deleting shot');
	}
}).catch((error) => {
	logger.error(error);
});

export const DuplicateShot = (token: string, shotId: number, order: number, session: string) => fetch(`${getApiHost()}/v2/greenroom/shots/duplicate`, {
	method: 'POST',
	body: JSON.stringify({ shotId, order, session }),
	...getAuthenticatedRequestInit(token),
}).then(async (result) => {
	if (result.ok) {
		return result.json();
	}
	throw new Error('Failed to duplicate shot');
}).catch((error) => {
	logger.error(error);
});

export const PutCanvasStyle = (token: string, meetingId: number, canvasStyle: AppSettings) => fetch(`${getApiHost()}/v2/greenroom/shots/style`, {
	method: 'PUT',
	body: JSON.stringify({ meetingId, canvasStyle }),
	...getAuthenticatedRequestInit(token),
}).catch((error) => {
	logger.error(error);
});

/**
 * Result of persisting the current shot id to greenroom-sync.
 * `stale` is true when the server rejected the write with HTTP 409 because this
 * client's cached entity version is behind the authoritative one — i.e. the
 * producer session is stale and must resync before its advances will land.
 * Previously this arrived as a generic 500 and was silently dropped (BDEV-4757),
 * so the slide changed locally only and never reached attendees.
 */
export interface UpdateCurrentShotResult {
	ok: boolean;
	stale: boolean;
}

export const updateCurrentShotIdEntity = async (
	token: string,
	roomId: number,
	newCurrentShotIdEntity: Entity,
	socketId: string,
	isPlaying: boolean,
): Promise<UpdateCurrentShotResult> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/current-shot-id`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
			body: JSON.stringify({ newCurrentShotIdEntity, socketId, isPlaying }),
		});
		if (res.ok) {
			return { ok: true, stale: false };
		}
		// 409 STALE_SESSION: our cached version is behind the server. Flag `stale`
		// so the caller can re-fetch the authoritative entity and retry, instead of
		// leaving the producer in a broken local-only state until a full reload.
		return { ok: false, stale: res.status === 409 };
	} catch (error) {
		logger.error(error);
	}
	return { ok: false, stale: false };
};

export const updateShotStartTimestampEntity = async (
	token: string,
	roomId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/shot-start-timestamp`);
		const res = await fetch(url, {
			method: HttpRequest.Put,
			headers: getSyncHeaders(token),
		});
		if (res.ok) {
			return true;
		}
	} catch (error) {
		logger.error(error);
	}
	return false;
};

export const clearShotStartTimestampEntity = async (
	token: string,
	roomId: number,
): Promise<boolean> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/clear-shot-start-timestamp`);
		const res = await fetch(url, {
			method: HttpRequest.Post,
			headers: getSyncHeaders(token),
		});
		if (res.ok) {
			return true;
		}
	} catch (error) {
		logger.error(error);
	}
	return false;
};

export const getShotStartTimestampEntity = async (
	token: string,
	roomId: number,
): Promise<Entity | null> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/shot-start-time-update`);
		const res = await fetch(url, {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});
		const entity = await res.json();
		return entity;
	} catch (error) {
		logger.error(error);
		return null;
	}
};

export const BulkDeleteShots = async (
	roomId: number,
	shotIds: number[],
): Promise<boolean> => {
	try {
		await blpAuth.post({
			host: getSyncHost(),
			url: `/v1/room/${roomId}/shots/bulk-delete`,
			token: blpAuth.accessToken,
			options: { headers: getEmbedSafeHeaders() },
			body: { shotIds },
		});
		return true;
	} catch (error) {
		logger.error('Error bulk deleting shots:', error);
		return false;
	}
};

export const getCurrentShotIdEntity = async (
	token: string,
	roomId: number,
): Promise<Entity | null> => {
	try {
		const url = buildSyncUrl(`v1/room/${roomId}/current-shot-id`);
		const res = await fetch(url, {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});
		if (res.status === 200) {
			const entity = await res.json();
			return entity;
		} else {
			return null;
		}
	} catch (error) {
		logger.error(error);
		return null;
	}
};
